import { resolveShiftDateForConfirm } from "../../../utils/timesheetConfirmDetails";

/** Score at or above this value is shown as flagged in the table. */
export const AI_ANOMALY_FLAG_THRESHOLD = 0.5;

/** Readiness at or above 90% → Passed (table + panel). Below 90% → Review. */
export const AI_READINESS_PASSED_THRESHOLD = 0.9;

/** Raw readiness → rounded 0–10 score (display only). */
export const readinessOutOf10 = (readinessScore) => {
  const raw = Number(readinessScore);
  if (!Number.isFinite(raw)) return null;
  const clamped = Math.max(0, Math.min(1, raw));
  return Math.max(0, Math.min(10, Math.round(clamped * 10)));
};

export const isReadinessPassed = (readinessScore) => {
  const raw = Number(readinessScore);
  return Number.isFinite(raw) && raw >= AI_READINESS_PASSED_THRESHOLD;
};

/** User-facing copy for analyse API/SSE error codes (no provider names). */
export const TIMESHEET_AI_ERROR_MESSAGES = {
  AI_CREDITS_EXHAUSTED:
    "Insufficient AI credits. Please top up and try again.",
  AI_PII_BLOCKED:
    "Analysis was stopped because personal information may still be present. Please review the notes and try again.",
  AI_LLM_ERROR:
    "AI analysis is temporarily unavailable. Please try again shortly.",
  AI_PARSE_ERROR:
    "AI analysis could not be completed. Please try again.",
  AI_STORAGE_ERROR:
    "The analysis finished but could not be saved. Contact support if this persists.",
  AI_UNAVAILABLE:
    "AI analysis is temporarily unavailable. Please try again or contact support.",
  AI_DEV_FALLBACK:
    "AI analysis is not available in this environment. Please contact support.",
};

const INTERNAL_AI_ERROR_HINT =
  /openai|language model|api[_ -]?key|server configuration|development placeholder/i;

/** Resolve toast/modal copy from an error object or batch result item. */
export const resolveTimesheetAiErrorMessage = (error, fallback = "") => {
  if (Number(error?.status) === 403) {
    return "This AI feature is disabled for your account.";
  }
  const code = String(error?.code || "").trim();
  if (code && TIMESHEET_AI_ERROR_MESSAGES[code]) {
    return TIMESHEET_AI_ERROR_MESSAGES[code];
  }
  const message = String(error?.message || fallback || "").trim();
  if (message && !INTERNAL_AI_ERROR_HINT.test(message)) {
    return message;
  }
  return TIMESHEET_AI_ERROR_MESSAGES.AI_UNAVAILABLE;
};

/** @deprecated use resolveTimesheetAiErrorMessage */
export const getTimesheetAiErrorToast = resolveTimesheetAiErrorMessage;

export const AI_REPORT_TAB_TO_DETAIL_ID = {
  TimesheetDetails: 1,
  ShiftTasks: 2,
  Medication: 3,
  Attachments: 4,
  Location: 5,
  ShiftEndQuestion: 8,
};

export const AI_REPORT_TAB_LABELS = {
  TimesheetDetails: "Timesheet Details",
  ShiftTasks: "Shift Tasks",
  Medication: "Medication",
  Attachments: "Attachments / Signature",
  Location: "Location",
  ShiftEndQuestion: "Shift End Questions",
};

/**
 * Code Allocation / NDIS claim checks — not part of timesheet analysis.
 * Code Allocation lives under Invoicing; strip from live + legacy reports.
 */
export const CODE_ALLOCATION_CHECK_IDS = new Set([
  "ndis_lines_vs_notes",
  "billing_breakdown_vs_reference",
  "budget_plain_english",
  "billing_plain_english",
]);

export const isCodeAllocationAiCheck = (checkOrId) => {
  const id =
    typeof checkOrId === "string" ? checkOrId : checkOrId?.id || "";
  return CODE_ALLOCATION_CHECK_IDS.has(id);
};

/**
 * Legacy check ids still present on stored reports / older anomaly_types.
 * Map to the canonical id used by new analyses and the catalogue.
 */
export const AI_CHECK_ID_ALIASES = {
  notes_vs_tasks: "shift_task_completion",
};

export const canonicalizeAiCheckId = (checkOrId) => {
  const id =
    typeof checkOrId === "string" ? checkOrId : checkOrId?.id || "";
  if (!id) return "";
  return AI_CHECK_ID_ALIASES[id] || id;
};

const canonicalizeAiCheck = (check) => {
  if (!check || typeof check !== "object") return check;
  const nextId = canonicalizeAiCheckId(check.id);
  if (!nextId || nextId === check.id) return check;
  return { ...check, id: nextId };
};

/** What each analyse check looks for — shown in the report UI when detail is thin. */
export const AI_CHECK_CATALOGUE = {
  note_quality: {
    label: "Shift note quality",
    description: "Checks for a missing, very short, or generic shift note.",
    tab: "TimesheetDetails",
    level: "participant",
  },
  goal_linkage: {
    label: "NDIS goal linkage",
    description:
      "Independently checks whether the participant's shift note references their funded NDIS goals.",
    tab: "TimesheetDetails",
    level: "participant",
  },
  notes_vs_mar: {
    label: "Medication administration",
    description:
      "Verifies MAR entries are recorded as Given or Refused — not compared to shift notes.",
    tab: "Medication",
    level: "participant",
  },
  shift_end_questions_complete: {
    label: "Shift end questions complete",
    description:
      "Checks whether all required shift end questions are answered (low severity when incomplete).",
    tab: "ShiftEndQuestion",
    level: "participant",
  },
  shift_end_injury: {
    label: "Shift end questions",
    description:
      "Reads shift end answers, including free text, and flags wording that describes an injury, pain, a fall, or a change in physical condition. That timesheet is queued in Care Watch.",
    tab: "ShiftEndQuestion",
    level: "participant",
  },
  notes_vs_end_questions: {
    label: "Shift end questions complete",
    description: "Checks whether all required shift end questions are answered.",
    tab: "ShiftEndQuestion",
    level: "participant",
  },
  incident_vs_notes: {
    label: "Incident reporting",
    description:
      "Reads mandatory incident details (one field per shift). If an incident is reported, checks forms are submitted — not compared to shift notes for pass/fail.",
    tab: "TimesheetDetails",
    level: "participant",
  },
  restrictive_practice_language: {
    label: "Restrictive practice language",
    description:
      "AI reviews each shift note and incident details for NDIS restrictive practices / restrictive factors (seclusion, chemical, mechanical, physical, environmental). High severity — verify BSP authorisation and reporting.",
    tab: "TimesheetDetails",
    level: "participant",
  },
  shift_task_completion: {
    label: "Shift task completion",
    description:
      "Checks participant-scoped and shift-level tasks. Incomplete mandatory tasks are Critical; optional or legacy incomplete tasks are a medium review. Not compared to shift notes.",
    tab: "ShiftTasks",
    level: "timesheet",
  },
  signature_attachments: {
    label: "Signature / attachments",
    description:
      "Checks participant or guardian signature when required by provider settings, plus photos and evidence for outings.",
    tab: "Attachments",
    level: "timesheet",
  },
  location_km_vs_notes: {
    label: "Location / KM vs notes",
    description:
      "When a KM amount is claimed, checks shift notes describe travel. GPS distance without KM is a separate medium review — not Critical.",
    tab: "TimesheetDetails",
    level: "timesheet",
  },
  km_vs_google_distance: {
    label: "KM vs Google Maps",
    description:
      "When Google Maps auto-calculated KM is available, compares it to the claimed KM (0.1 km or 5% tolerance).",
    tab: "TimesheetDetails",
    level: "timesheet",
  },
  group_shift_ratio: {
    label: "Group shift",
    description:
      "Checks each participant has their own shift note on group shifts.",
    tab: "TimesheetDetails",
    level: "timesheet",
  },
  shift_instructions_compliance: {
    label: "Shift instructions compliance",
    description: "Checks coordinator instructions are reflected in shift notes.",
    tab: "TimesheetDetails",
    level: "timesheet",
  },
  billing_times_vs_schedule: {
    label: "Roster vs actual times",
    description:
      "Checks worker clock-in/out are within 15 min of roster (late start or early finish).",
    tab: "TimesheetDetails",
    level: "timesheet",
  },
  schedule_vs_actual: {
    label: "Schedule vs actual",
    description: "Checks actual hours vs schedule and whether variation is explained.",
    tab: "TimesheetDetails",
    level: "timesheet",
  },
  generic_duplicate_notes: {
    label: "Generic / duplicate notes",
    description: "Flags copy-pasted or near-duplicate shift notes across shifts or participants.",
    tab: "TimesheetDetails",
    level: "timesheet",
  },
};

export const AI_CHECK_STATUS_ORDER = { fail: 0, warn: 1, pass: 2, skipped: 3 };

export const AI_CHECK_STATUS_STYLES = {
  pass: {
    label: "Pass",
    tone: "emerald",
    dot: "bg-emerald-500",
    badge: "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200",
    border: "border-emerald-100",
    iconBg: "bg-emerald-50 text-emerald-600",
  },
  warn: {
    label: "Review",
    tone: "amber",
    dot: "bg-amber-500",
    badge: "bg-amber-50 text-amber-900 ring-1 ring-amber-200",
    border: "border-amber-100",
    iconBg: "bg-amber-50 text-amber-600",
  },
  fail: {
    label: "Critical",
    tone: "red",
    dot: "bg-red-500",
    badge: "bg-red-50 text-red-800 ring-1 ring-red-200",
    border: "border-red-100",
    iconBg: "bg-red-50 text-red-600",
  },
  skipped: {
    label: "Skipped",
    tone: "gray",
    dot: "bg-gray-400",
    badge: "bg-gray-50 text-gray-600 ring-1 ring-gray-200",
    border: "border-gray-100",
    iconBg: "bg-gray-50 text-gray-500",
  },
};

export const AI_SEVERITY_ORDER = { high: 0, medium: 1, low: 2 };

export const normalizeAiCheckSeverity = (check) =>
  String(check?.severity || "low").toLowerCase();

/** Critical = failed check with high severity only (legacy fail without severity counts as critical). */
export const isCriticalAiCheck = (check) => {
  const status = String(check?.status || "").toLowerCase();
  if (status !== "fail") return false;
  if (check?.severity == null || check?.severity === "") {
    return true;
  }
  return normalizeAiCheckSeverity(check) === "high";
};

/** Review = any other failed or warning check. */
export const isReviewAiCheck = (check) => {
  const status = String(check?.status || "").toLowerCase();
  if (status !== "fail" && status !== "warn") return false;
  return !isCriticalAiCheck(check);
};

/** Badge label + tone from status and severity (not status alone). */
export const resolveAiCheckBadge = (check) => {
  if (isCriticalAiCheck(check)) {
    return { label: "Critical", tone: "red", statusKey: "fail" };
  }
  const status = String(check?.status || "skipped").toLowerCase();
  if (status === "fail" || status === "warn") {
    return { label: "Review", tone: "amber", statusKey: "warn" };
  }
  const style = AI_CHECK_STATUS_STYLES[status] || AI_CHECK_STATUS_STYLES.skipped;
  return { label: style.label, tone: style.tone, statusKey: status };
};

/** From Laravel get-worker-timesheets row.ai_anomaly */
export const buildAiStatusFromRow = (aiAnomaly) => {
  if (!aiAnomaly?.has_report) {
    return null;
  }

  if (aiAnomaly.check_summary || Array.isArray(aiAnomaly.top_issues)) {
    return {
      hasReport: true,
      failed: false,
      anomaly_score: aiAnomaly.anomaly_score,
      readiness_score: aiAnomaly.readiness_score,
      ready_to_approve: aiAnomaly.ready_to_approve,
      risk_level: aiAnomaly.risk_level,
      reject_probability: aiAnomaly.reject_probability,
      headline: stripAiMarkdown(aiAnomaly.headline || ""),
      checkSummary: aiAnomaly.check_summary || null,
      topIssues: aiAnomaly.top_issues || [],
    };
  }

  let reportRaw = aiAnomaly?.report;
  if (typeof reportRaw === "string") {
    try {
      reportRaw = JSON.parse(reportRaw);
    } catch {
      reportRaw = null;
    }
  }

  if (reportRaw && typeof reportRaw === "object") {
    const normalized = applyComputedOverallScores(
      normalizeAiReportPayload({
        report: reportRaw,
        reasoning: aiAnomaly?.reasoning,
        anomaly_score: aiAnomaly?.anomaly_score,
        anomaly_types: aiAnomaly?.anomaly_types,
        updated_at: aiAnomaly?.analyzed_at,
      })
    );
    const headline = stripAiMarkdown(
      normalized?.overall?.headline || aiAnomaly?.reasoning || ""
    );
    if (!isDevFallbackAiContent(headline)) {
      const allChecks = collectAllChecksFromReport(normalized);
      const checkSummary = summarizeAiChecks(allChecks);
      const topIssues = getTopAiCheckIssues(normalized, 3);
      return {
        hasReport: true,
        failed: false,
        anomaly_score: normalized?.anomaly_score ?? aiAnomaly?.anomaly_score,
        anomaly_types: aiAnomaly?.anomaly_types || [],
        analyzed_at: aiAnomaly?.analyzed_at || null,
        headline,
        checkSummary,
        topIssues,
        topIssuesLine: formatTopAiIssuesLine(topIssues),
        ready_to_approve: normalized?.overall?.ready_to_approve,
        readiness_score: normalized?.overall?.readiness_score,
        risk_level: normalized?.overall?.risk_level,
        reject_probability: normalized?.overall?.reject_probability,
      };
    }
  }

  const reasoning = stripAiMarkdown(aiAnomaly?.reasoning || "");
  if (isDevFallbackAiContent(reasoning)) {
    return {
      hasReport: false,
      failed: true,
      failMessage: TIMESHEET_AI_ERROR_MESSAGES.AI_DEV_FALLBACK,
      code: "AI_DEV_FALLBACK",
    };
  }

  return {
    hasReport: true,
    failed: false,
    anomaly_score: aiAnomaly?.anomaly_score,
    anomaly_types: aiAnomaly?.anomaly_types || [],
    analyzed_at: aiAnomaly?.analyzed_at || null,
    headline: reasoning || undefined,
    readiness_score:
      Number.isFinite(Number(aiAnomaly?.anomaly_score))
        ? Math.max(0, Math.min(1, 1 - Number(aiAnomaly.anomaly_score)))
        : null,
  };
};

/** Session override wins, except stale AI_DEV_FALLBACK when Laravel has a real report. */
export const resolveAiStatusForTimesheet = (
  timesheetId,
  rowAiAnomaly,
  sessionOverrides = {}
) => {
  const id = Number(timesheetId);
  const rowStatus = buildAiStatusFromRow(rowAiAnomaly);
  const sessionStatus = sessionOverrides[id];

  if (
    sessionStatus?.code === "AI_DEV_FALLBACK" &&
    rowStatus?.hasReport &&
    !rowStatus?.failed
  ) {
    return rowStatus;
  }

  if (
    sessionStatus?.failed &&
    rowStatus?.hasReport &&
    !rowStatus?.failed
  ) {
    return rowStatus;
  }

  if (sessionStatus) {
    return sessionStatus;
  }

  return rowStatus;
};

/**
 * Normalise stored or live analyse payload (Laravel read + Python run).
 * Supports nested `report` JSON and legacy flat fields.
 */
export const normalizeAiReportPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  const nested =
    payload.report && typeof payload.report === "object" ? payload.report : {};
  const rawChecks = Array.isArray(nested.checks)
    ? nested.checks
    : Array.isArray(payload.checks)
      ? payload.checks
      : [];
  const rawParticipants = Array.isArray(nested.participants)
    ? nested.participants
    : Array.isArray(payload.participants)
      ? payload.participants
      : [];
  const checks = filterChecksForTimesheetScope(rawChecks).map(canonicalizeAiCheck);
  const participants = rawParticipants.map((participant) => ({
    ...participant,
    checks: filterChecksForTimesheetScope(participant?.checks || []).map(
      canonicalizeAiCheck
    ),
  }));
  const overall = nested.overall || payload.overall || null;
  const rawPredictions = nested.predictions || payload.predictions || null;

  return applyComputedOverallScores({
    schemaVersion: nested.schema_version || payload.schema_version || "1.0",
    // Funding / NDIS claim gates live on Code Allocation — never show here.
    fundingChecksEnabled: false,
    overall,
    summary: overall?.summary || "",
    checks,
    participants,
    suggestions: nested.suggestions || payload.suggestions || [],
    predictions: filterPredictionsForTimesheetScope(rawPredictions),
    stale: Boolean(payload.stale || nested.stale),
    anomaly_score: payload.anomaly_score,
    anomaly_types: (payload.anomaly_types || [])
      .map(canonicalizeAiCheckId)
      .filter((id) => id && !isCodeAllocationAiCheck(id)),
    reasoning:
      payload.reasoning ||
      overall?.headline ||
      "",
    analyzed_at:
      payload.analyzed_at ||
      payload.updated_at ||
      payload.created_at ||
      nested.analyzed_at ||
      null,
    raw: payload,
  });
};

/** Mirror server-side severity weights for readiness (skipped checks excluded). */
const CHECK_READINESS_WEIGHT = {
  pass: 1.0,
  "warn:low": 0.92,
  "warn:medium": 0.85,
  "warn:high": 0.45,
  "fail:low": 0.35,
  "fail:medium": 0.25,
  "fail:high": 0.0,
};

const issuePenaltyFromChecks = (checks = []) => {
  let highFails = 0;
  let mediumFails = 0;
  let highWarns = 0;
  let mediumWarns = 0;
  let lowWarns = 0;

  checks.forEach((check) => {
    const status = String(check?.status || "").toLowerCase();
    const severity = String(check?.severity || "low").toLowerCase();
    if (status === "skipped" || status === "pass") return;
    if (status === "fail" && severity === "high") highFails += 1;
    else if (status === "fail" && severity === "medium") mediumFails += 1;
    else if (status === "warn" && severity === "high") highWarns += 1;
    else if (status === "warn" && severity === "medium") mediumWarns += 1;
    else if (status === "warn" && severity === "low") lowWarns += 1;
  });

  return Math.min(
    0.9,
    0.22 * highFails +
      0.1 * mediumFails +
      0.12 * highWarns +
      0.05 * mediumWarns +
      0.02 * lowWarns
  );
};

/** Recompute readiness/reject from gated checks (fixes stale overall blocks). */
export const computeOverallScoresFromChecks = (checks = []) => {
  const scored = checks.filter(
    (c) => String(c?.status || "").toLowerCase() !== "skipped"
  );
  if (!scored.length) {
    return { readiness_score: null, reject_probability: null };
  }

  const weights = scored.map((check) => {
    const status = String(check?.status || "").toLowerCase();
    const severity = String(check?.severity || "low").toLowerCase();
    if (status === "pass") return 1.0;
    const key = `${status}:${severity}`;
    return CHECK_READINESS_WEIGHT[key] ?? 0.5;
  });

  const readinessRaw = weights.reduce((a, b) => a + b, 0) / weights.length;
  const highFails = scored.filter(
    (c) =>
      String(c?.status || "").toLowerCase() === "fail" &&
      String(c?.severity || "").toLowerCase() === "high"
  ).length;
  const penalty = issuePenaltyFromChecks(scored);
  let reject = Math.max(1 - readinessRaw, penalty);
  if (!highFails) {
    reject = Math.min(reject, 0.4);
  }
  reject = Math.min(1, Math.max(0, Math.round(reject * 100) / 100));
  const readiness = Math.round((1 - reject) * 100) / 100;
  return { readiness_score: readiness, reject_probability: reject };
};

export const applyComputedOverallScores = (normalized) => {
  if (!normalized) return normalized;
  const allChecks = collectAllChecksFromReport(normalized);
  const computed = computeOverallScoresFromChecks(allChecks);
  if (!Number.isFinite(computed.readiness_score)) return normalized;

  const failCount = allChecks.filter((c) => c.status === "fail").length;
  const warnCount = allChecks.filter((c) => c.status === "warn").length;
  const criticalCount = allChecks.filter(isCriticalAiCheck).length;
  const needsReview = failCount > 0 || warnCount > 0;
  const highFails = allChecks.filter(isCriticalAiCheck).length;
  const rejectProb = computed.reject_probability;
  const riskLevel =
    highFails >= 2 || rejectProb >= 0.55
      ? "high"
      : needsReview
        ? "medium"
        : "low";
  const predictedOutcome =
    highFails >= 2 || rejectProb >= 0.55
      ? "likely_reject"
      : needsReview
        ? "needs_review"
        : "likely_approve";

  const otherIssues = Math.max(failCount + warnCount - criticalCount, 0);
  const readinessPassed = isReadinessPassed(computed.readiness_score);
  let headline = normalized.overall?.headline;
  if (criticalCount > 0) {
    headline = `${criticalCount} critical issue${criticalCount !== 1 ? "s" : ""}${
      otherIssues ? ` and ${otherIssues} to review` : ""
    } before approval`;
  } else if (readinessPassed && needsReview) {
    headline = `Passed — ${failCount + warnCount} item(s) to fix before approval`;
  } else if (needsReview) {
    headline = `${failCount + warnCount} item(s) to review before approval`;
  }

  return {
    ...normalized,
    overall: {
      ...(normalized.overall || {}),
      readiness_score: computed.readiness_score,
      reject_probability: computed.reject_probability,
      risk_level: riskLevel,
      predicted_outcome: predictedOutcome,
      ready_to_approve:
        criticalCount === 0 && isReadinessPassed(computed.readiness_score),
      headline: headline || normalized.overall?.headline,
    },
    anomaly_score: computed.reject_probability,
  };
};

/** Correct known stale LLM check shapes until the report is re-analysed on the server. */
export const reconcileStaleCheck = (check) => {
  if (!check || typeof check !== "object") return check;
  const summary = String(check.summary || "").toLowerCase();
  if (check.id === "goal_linkage") {
    if (
      check.status === "pass" &&
      (summary.includes("no funded goals") ||
        summary.includes("goal linkage not required"))
    ) {
      return {
        ...check,
        status: "warn",
        severity: "medium",
        summary: check.summary?.includes("does not have any goals")
          ? check.summary
          : "The participant does not have any goals.",
      };
    }
    if (check.status === "warn" || check.status === "fail") {
      if (
        summary.includes("note is empty") ||
        summary.includes("shift note is empty") ||
        summary.includes("no shift note") ||
        summary.includes("note missing")
      ) {
        return {
          ...check,
          status: "pass",
          severity: "low",
          summary:
            check.summary ||
            "Participant shift note missing — goal linkage assessed once a note is added.",
        };
      }
    }
  }
  return check;
};

/** Merge catalogue metadata into a check for richer UI display. */
export const enrichAiCheck = (check) => {
  if (!check || typeof check !== "object") return check;
  const reconciled = reconcileStaleCheck(canonicalizeAiCheck(check));
  const meta = AI_CHECK_CATALOGUE[reconciled.id] || {};
  return {
    ...reconciled,
    label: meta.label || reconciled.label || humanizeCheckId(reconciled.id),
    tab: reconciled.tab || meta.tab,
    description: meta.description || "",
    level: meta.level || "timesheet",
  };
};

/** All checks from a normalised report (timesheet + every participant). */
export const collectAllChecksFromReport = (normalized) => {
  if (!normalized) return [];
  const timesheetChecks = filterChecksForTimesheetScope(
    (normalized.checks || []).map(enrichAiCheck)
  );
  const participantChecks = (normalized.participants || []).flatMap((participant) =>
    filterChecksForTimesheetScope(participant.checks || []).map((check) =>
      enrichAiCheck({
        ...check,
        participant_index: participant.participant_index,
        participant_label: participant.display_name,
      })
    )
  );
  return [...timesheetChecks, ...participantChecks];
};

/** Top warn/fail items for compact UI (table tooltip, results row). */
export const getTopAiCheckIssues = (normalized, limit = 3) => {
  const all = collectAllChecksFromReport(normalized);
  return all
    .filter((c) => c.status === "fail" || c.status === "warn")
    .sort((a, b) => {
      const criticalA = isCriticalAiCheck(a) ? 0 : 1;
      const criticalB = isCriticalAiCheck(b) ? 0 : 1;
      if (criticalA !== criticalB) return criticalA - criticalB;
      const sevA = AI_SEVERITY_ORDER[normalizeAiCheckSeverity(a)] ?? 9;
      const sevB = AI_SEVERITY_ORDER[normalizeAiCheckSeverity(b)] ?? 9;
      if (sevA !== sevB) return sevA - sevB;
      const orderA = AI_CHECK_STATUS_ORDER[a.status] ?? 9;
      const orderB = AI_CHECK_STATUS_ORDER[b.status] ?? 9;
      if (orderA !== orderB) return orderA - orderB;
      return String(a.label || "").localeCompare(String(b.label || ""));
    })
    .slice(0, limit)
    .map((c) => ({
      id: c.id,
      label: c.label,
      status: c.status,
      severity: normalizeAiCheckSeverity(c),
      badge: resolveAiCheckBadge(c).label,
      summary: c.summary || c.description || "",
      participant_label: c.participant_label,
    }));
};

export const formatTopAiIssuesLine = (issues = []) => {
  if (!issues.length) return "";
  return issues
    .map((item) => {
      const prefix = item.badge || resolveAiCheckBadge(item).label;
      const who = item.participant_label ? `${item.participant_label}: ` : "";
      const text = truncateAiText(stripAiMarkdown(item.summary || item.label), 80);
      return prefix ? `${prefix} · ${who}${text}` : `${who}${text}`;
    })
    .join(" · ");
};

/** Readiness chip styling for a participant. */
export const AI_PARTICIPANT_READINESS = {
  ready: { label: "Ready", tone: "emerald", badge: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  needs_review: { label: "Needs review", tone: "amber", badge: "bg-amber-50 text-amber-900 ring-amber-200" },
  not_ready: { label: "Critical", tone: "red", badge: "bg-red-50 text-red-800 ring-red-200" },
};

/** Group a flat check list into Fail → Warn → Pass → Skipped buckets. */
export const groupChecksByStatus = (checks = []) => {
  const groups = { fail: [], warn: [], pass: [], skipped: [] };
  sortAiChecks(checks).forEach((c) => {
    const status = String(c?.status || "skipped").toLowerCase();
    (groups[status] || groups.skipped).push(c);
  });
  return groups;
};

/** Whether a check has enough content to show in the UI (hide empty pass/skipped). */
export const isCheckActionable = (check) => {
  const status = String(check?.status || "skipped").toLowerCase();
  if (status === "fail" || status === "warn") return true;
  return Boolean(
    check?.summary ||
      check?.suggestion ||
      (Array.isArray(check?.evidence) && check.evidence.length > 0)
  );
};

/** Title-case person names for AI report UI (``john alex`` → ``John Alex``). */
export const titleCasePersonName = (name) => {
  if (!name || typeof name !== "string") return "";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

/** Human label for a participant — prefers rehydrated display_name from the report. */
export const formatParticipantLabel = (participant, index, lookup = {}) => {
  if (participant?.display_name) {
    return titleCasePersonName(participant.display_name);
  }
  const pid = participant?.participant_id;
  const puuid = participant?.participant_uuid;
  if (puuid && lookup[puuid]) {
    return titleCasePersonName(lookup[puuid]);
  }
  if (pid != null && lookup[pid]) {
    return titleCasePersonName(lookup[pid]);
  }
  const idx = participant?.participant_index ?? index + 1;
  if (lookup[`index:${idx}`]) {
    return titleCasePersonName(lookup[`index:${idx}`]);
  }
  return `Participant ${idx}`;
};

/** Build id/index/uuid → name map from Laravel timesheet row participants. */
export const buildParticipantNameLookup = (participants = []) => {
  const lookup = {};
  participants.forEach((p, i) => {
    const name = titleCasePersonName(
      [p?.first_name, p?.last_name].filter(Boolean).join(" ").trim()
    );
    if (!name) return;
    const pid = p?.participant_id ?? p?.id;
    if (pid != null) lookup[pid] = name;
    const puuid = p?.participant_uuid ?? p?.uuid;
    if (puuid) lookup[puuid] = name;
    lookup[`index:${i + 1}`] = name;
  });
  return lookup;
};

export const summarizeAiChecks = (checks = []) => {
  const summary = {
    pass: 0,
    warn: 0,
    fail: 0,
    skipped: 0,
    critical: 0,
    review: 0,
    total: checks.length,
  };
  checks.forEach((check) => {
    const status = String(check?.status || "skipped").toLowerCase();
    if (summary[status] !== undefined) summary[status] += 1;
    if (isCriticalAiCheck(check)) {
      summary.critical += 1;
    } else if (isReviewAiCheck(check)) {
      summary.review += 1;
    }
  });
  summary.issues = summary.critical + summary.review;
  summary.evaluated = summary.total - summary.skipped;
  return summary;
};

/**
 * Deterministic compliance pass rate from gated checks (skipped checks excluded).
 * Used in Predictions as "Compliance check" percentage.
 */
export const computeComplianceCheckPercent = (checks = []) => {
  const summary = summarizeAiChecks(checks);
  if (!summary.evaluated || summary.evaluated <= 0) {
    return null;
  }
  const percent = Math.min(
    100,
    Math.max(0, Math.round((summary.pass / summary.evaluated) * 100))
  );
  return {
    percent,
    label: `${percent}%`,
    pass: summary.pass,
    evaluated: summary.evaluated,
    skipped: summary.skipped,
    total: summary.total,
    detail: `${summary.pass} of ${summary.evaluated} checks passed`,
  };
};

/** Funding AI is Code Allocation scope — never shown on timesheet analysis. */
export const isFundingAiEnabled = () => false;

/** Drop Code Allocation / NDIS claim checks from timesheet analysis UI. */
export const filterChecksForTimesheetScope = (checks = []) =>
  (Array.isArray(checks) ? checks : []).filter(
    (c) => !isCodeAllocationAiCheck(c)
  );

/** @deprecated use filterChecksForTimesheetScope — funding gates are out of timesheet scope. */
export const filterChecksForFundingScope = (checks = [], _normalized = null) =>
  filterChecksForTimesheetScope(checks);

/** Strip billing/funding prediction copy (owned by Code Allocation). */
export const filterPredictionsForTimesheetScope = (predictions) => {
  if (!predictions || typeof predictions !== "object") return predictions;
  return {
    ...predictions,
    billing_risk: "",
    funding_impact: "",
  };
};

/** @deprecated use filterPredictionsForTimesheetScope */
export const filterPredictionsForFundingScope = (
  predictions,
  _normalized = null
) => filterPredictionsForTimesheetScope(predictions);

/** Split checks into issues / passed / skipped for layered UI rendering. */
export const partitionChecks = (checks = [], _normalized = null) => {
  const scoped = filterChecksForTimesheetScope(checks);
  const groups = groupChecksByStatus(scoped);
  return {
    issues: [...groups.fail, ...groups.warn],
    passed: groups.pass,
    skipped: groups.skipped,
    issueCount: groups.fail.length + groups.warn.length,
  };
};

/** One-line participant status for accordion headers — avoids misleading "0/6 passed". */
export const formatParticipantSubtitle = (checks = [], readiness = "") => {
  const { pass, warn, fail, skipped, issues, evaluated, critical, review } =
    summarizeAiChecks(checks);

  if (issues > 0) {
    const parts = [];
    if (critical) parts.push(`${critical} critical`);
    if (review) parts.push(`${review} to review`);
    return `${issues} need${issues === 1 ? "s" : ""} attention · ${parts.join(" · ")}`;
  }

  if (readiness === "ready") {
    if (skipped > 0 && pass > 0) {
      return `${pass} passed · ${skipped} not applicable on this shift`;
    }
    if (pass > 0) {
      return `All ${pass} checks passed`;
    }
    return "No issues found";
  }

  if (evaluated > 0) {
    return `${pass} of ${evaluated} checks passed`;
  }
  return "No checks evaluated";
};

/** Hero / section subtitle for aggregate check counts. */
export const formatAggregateCheckLabel = (checks = []) => {
  const { pass, issues, skipped, evaluated } = summarizeAiChecks(checks);
  if (issues > 0) {
    return `${issues} item${issues === 1 ? "" : "s"} to fix · ${pass} passed`;
  }
  if (skipped > 0) {
    return `${pass} passed · ${skipped} not applicable`;
  }
  return `${pass} passed`;
};

/** Split passed checks into summary lines vs bare chips for layered UI. */
export const splitPassedChecks = (checks = []) => {
  const passed = (checks || []).filter(
    (c) => String(c?.status || "").toLowerCase() === "pass"
  );
  return {
    withSummary: passed.filter((c) => Boolean(c?.summary?.trim())),
    bare: passed.filter((c) => !c?.summary?.trim()),
  };
};

export const sortAiChecks = (checks = []) =>
  [...checks].sort((a, b) => {
    const orderA = AI_CHECK_STATUS_ORDER[a?.status] ?? 9;
    const orderB = AI_CHECK_STATUS_ORDER[b?.status] ?? 9;
    if (orderA !== orderB) return orderA - orderB;
    const criticalA = isCriticalAiCheck(a) ? 0 : 1;
    const criticalB = isCriticalAiCheck(b) ? 0 : 1;
    if (criticalA !== criticalB) return criticalA - criticalB;
    const sevA = AI_SEVERITY_ORDER[normalizeAiCheckSeverity(a)] ?? 9;
    const sevB = AI_SEVERITY_ORDER[normalizeAiCheckSeverity(b)] ?? 9;
    if (sevA !== sevB) return sevA - sevB;
    return String(a?.label || a?.id || "").localeCompare(
      String(b?.label || b?.id || "")
    );
  });

/**
 * Group checks for report drawer V2: Critical → Review → Passed → Skipped.
 */
export const triageAiChecks = (checks = []) => {
  const enriched = sortAiChecks((checks || []).map(enrichAiCheck));
  const critical = [];
  const review = [];
  const passed = [];
  const skipped = [];

  enriched.forEach((check) => {
    const status = String(check?.status || "").toLowerCase();
    if (isCriticalAiCheck(check)) {
      critical.push(check);
    } else if (isReviewAiCheck(check)) {
      review.push(check);
    } else if (status === "pass") {
      passed.push(check);
    } else {
      skipped.push(check);
    }
  });

  return { critical, review, passed, skipped };
};

export const resolveDetailTabIdForCheck = (check) => {
  const tabKey = check?.tab;
  if (!tabKey) return null;
  return AI_REPORT_TAB_TO_DETAIL_ID[tabKey] ?? null;
};

export const formatAiTabLabel = (tabKey) =>
  AI_REPORT_TAB_LABELS[tabKey] || tabKey || "Details";

export const formatAiPercent = (value, digits = 0) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${Math.round(number * (digits === 0 ? 100 : 100)) / (digits === 0 ? 1 : 1)}${digits === 0 ? "%" : ""}`;
};

export const formatReadinessPercent = (readiness) => {
  const number = Number(readiness);
  if (!Number.isFinite(number)) return null;
  return `${Math.round(number * 100)}%`;
};

/** Integer percents for readiness + reject risk that always sum to 100. */
export const formatComplementaryReadinessPercents = (readinessScore) => {
  const raw = Number(readinessScore);
  if (!Number.isFinite(raw)) {
    return { readiness: null, rejectRisk: null };
  }
  const readinessPct = Math.min(100, Math.max(0, Math.round(raw * 100)));
  return {
    readiness: `${readinessPct}%`,
    rejectRisk: `${100 - readinessPct}%`,
  };
};

/** Readiness 0..1 from status (prefer report score, not reject/anomaly alone). */
export const resolveReadinessScoreFromStatus = (status) => {
  if (!status || typeof status !== "object") return null;
  const direct = Number(status.readiness_score);
  if (Number.isFinite(direct)) {
    return Math.max(0, Math.min(1, direct));
  }
  const reject = Number(status.anomaly_score);
  if (Number.isFinite(reject)) {
    return Math.max(0, Math.min(1, 1 - reject));
  }
  return null;
};

/** Table compact score e.g. readiness 0.3 → "3/10". */
export const formatReadinessOutOf10 = (readinessScore) => {
  const raw = Number(readinessScore);
  if (!Number.isFinite(raw)) return null;
  const clamped = Math.max(0, Math.min(1, raw));
  const outOf10 = Math.max(0, Math.min(10, Math.round(clamped * 10)));
  return `${outOf10}/10`;
};

/** Table compact score e.g. readiness 0.85 → "85%". */
export const formatReadinessAsPercent = (readinessScore) => {
  const raw = Number(readinessScore);
  if (!Number.isFinite(raw)) return null;
  const clamped = Math.max(0, Math.min(1, raw));
  return `${Math.round(clamped * 100)}%`;
};

/** Derive risk level when stored overall block is missing or stale. */
export const resolveRiskLevelFromStatus = (status) => {
  const summary = status?.checkSummary;
  if (summary?.critical > 0) return "high";
  if (summary?.review > 0) return "medium";

  const explicit = String(status?.risk_level || "").toLowerCase();
  if (explicit === "high" || explicit === "medium" || explicit === "low") {
    return explicit;
  }

  const readiness = resolveReadinessScoreFromStatus(status);
  if (Number.isFinite(readiness)) {
    if (isReadinessPassed(readiness)) return "low";
    if (readiness > 0.5) return "medium";
    return "high";
  }

  if (status?.ready_to_approve === true) return "low";
  return "medium";
};

/**
 * Single source of truth for table icon, results panel, and tooltips.
 * 1. Any critical check (fail + high) → Critical
 * 2. Readiness 90%+ → Passed (review items may still be shown)
 * 3. Otherwise → Review
 */
export const resolveAiDisplayState = ({
  failed = false,
  failMessage,
  readiness_score,
  checkSummary = null,
  checks = [],
} = {}) => {
  if (failed) {
    return {
      key: "failed",
      shortLabel: "Unavailable",
      label: "Analysis unavailable",
      tone: "red",
      flagged: true,
      scoreText: "—/10",
      failMessage,
      reviewCount: 0,
      hasReviewItems: false,
    };
  }

  const summary =
    checkSummary && typeof checkSummary === "object" && summaryHasTotals(checkSummary)
      ? checkSummary
      : summarizeAiChecks(checks);
  const readiness = Number(readiness_score);
  const resolvedReadiness = Number.isFinite(readiness)
    ? readiness
    : resolveReadinessScoreFromStatus({ readiness_score });
  const scoreText = formatReadinessOutOf10(resolvedReadiness) || "—/10";
  const reviewCount = Number(summary.review) || 0;

  if ((summary.critical || 0) > 0) {
    return {
      key: "critical",
      shortLabel: "Critical",
      label: "Critical issues",
      tone: "red",
      flagged: true,
      scoreText,
      readiness: resolvedReadiness,
      checkSummary: summary,
      reviewCount,
      hasReviewItems: reviewCount > 0,
    };
  }

  const passed = isReadinessPassed(resolvedReadiness);

  if (passed) {
    return {
      key: "passed",
      shortLabel: "Passed",
      label: reviewCount > 0 ? "Passed — items to fix" : "All checks passed",
      tone: "emerald",
      flagged: false,
      scoreText,
      readiness: resolvedReadiness,
      checkSummary: summary,
      reviewCount,
      hasReviewItems: reviewCount > 0,
    };
  }

  return {
    key: "review",
    shortLabel: "Review",
    label: "Needs review",
    tone: "amber",
    flagged: true,
    scoreText,
    readiness: resolvedReadiness,
    checkSummary: summary,
    reviewCount,
    hasReviewItems: reviewCount > 0,
  };
};

const summaryHasTotals = (summary) =>
  summary.total != null || summary.evaluated != null || summary.pass != null;

/**
 * Table AI column: icon colour + label (delegates to resolveAiDisplayState).
 */
export const resolveAiTableVisualState = (status) => {
  if (!status) {
    return { scoreText: null, label: null, tone: "gray", flagged: false };
  }

  const display = resolveAiDisplayState({
    failed: status.failed,
    failMessage: status.failMessage,
    readiness_score: status.readiness_score,
    checkSummary: status.checkSummary,
  });

  return {
    scoreText: display.scoreText,
    label: display.shortLabel,
    tone: display.tone,
    flagged: display.flagged,
    fixCount: display.hasReviewItems ? display.reviewCount : 0,
  };
};

/** Score + Passed / Review / Critical label for the timesheet table AI column. */
export const resolveAiTableScoreLabel = (status) => {
  const visual = resolveAiTableVisualState(status);
  return {
    scoreText: visual.scoreText,
    label: visual.label,
    tone: visual.tone,
    fixCount: visual.fixCount || 0,
  };
};

export const buildAiStatusFromBatchItem = (item) => {
  if (item?.success === true) {
    const data = item?.data || {};
    const normalized = applyComputedOverallScores(normalizeAiReportPayload(data));
    const headline = stripAiMarkdown(
      normalized?.overall?.headline || data?.reasoning || ""
    );
    if (isDevFallbackAiContent(headline)) {
      return {
        hasReport: false,
        failed: true,
        failMessage: TIMESHEET_AI_ERROR_MESSAGES.AI_DEV_FALLBACK,
        code: "AI_DEV_FALLBACK",
      };
    }
    const allChecks = collectAllChecksFromReport(normalized);
    const checkSummary = summarizeAiChecks(allChecks);
    const topIssues = getTopAiCheckIssues(normalized, 3);
    return {
      hasReport: true,
      failed: false,
      anomaly_score: normalized?.anomaly_score ?? data?.anomaly_score,
      anomaly_types: data?.anomaly_types || [],
      analyzed_at: new Date().toISOString(),
      headline,
      checkSummary,
      topIssues,
      topIssuesLine: formatTopAiIssuesLine(topIssues),
      ready_to_approve: normalized?.overall?.ready_to_approve,
      readiness_score: normalized?.overall?.readiness_score,
      risk_level: normalized?.overall?.risk_level,
      reject_probability: normalized?.overall?.reject_probability,
    };
  }
  return {
    hasReport: false,
    failed: true,
    failMessage: resolveTimesheetAiErrorMessage(
      item,
      "AI analysis could not be completed."
    ),
    code: item?.code || "",
  };
};

export const mergeBatchResultsIntoAiStatus = (prev, batchResult) => {
  const next = { ...prev };
  (batchResult?.results || []).forEach((item) => {
    const id = Number(item?.timesheet_id);
    if (!Number.isFinite(id) || id <= 0) return;

    if (item?.success !== true) {
      const existing = prev[id];
      if (existing?.hasReport && !existing?.failed) {
        return;
      }
    }

    next[id] = buildAiStatusFromBatchItem(item);
  });
  return next;
};

export const isAiAnomalyFlagged = (status) => {
  const score = Number(status?.anomaly_score);
  return Number.isFinite(score) && score >= AI_ANOMALY_FLAG_THRESHOLD;
};

export const formatAiScore = (score) => {
  const number = Number(score);
  if (!Number.isFinite(number)) return "N/A";
  return number.toFixed(2);
};

/**
 * UI state from E3 `overall` block, with legacy score fallback.
 * Uses resolveAiDisplayState so panel matches the timesheet table column.
 */
export const resolveAiOverallUiState = ({
  overall,
  checks = [],
  checkSummary = null,
  anomaly_score,
  failed = false,
  failMessage,
} = {}) => {
  if (failed) {
    return {
      key: "failed",
      label: "Analysis unavailable",
      shortLabel: "Unavailable",
      description: failMessage || "AI could not complete this check.",
      scoreHint: null,
      tone: "red",
    };
  }

  const summary =
    checkSummary && summaryHasTotals(checkSummary)
      ? checkSummary
      : summarizeAiChecks(checks);
  const readiness = Number(overall?.readiness_score);
  const rejectProb = Number(overall?.reject_probability);
  const headline = overall?.headline || "";

  const display = resolveAiDisplayState({
    failed: false,
    readiness_score: readiness,
    checkSummary: summary,
    checks,
  });

  if (display.key === "critical") {
    const critical = summary.critical || 0;
    return {
      key: "critical",
      label: display.label,
      shortLabel: display.shortLabel,
      description:
        headline ||
        `${critical} critical check${critical !== 1 ? "s" : ""} — resolve before approval.`,
      scoreHint: Number.isFinite(rejectProb)
        ? `Reject risk ${formatReadinessPercent(rejectProb)}`
        : null,
      tone: "red",
      readiness,
      rejectProb,
      risk: overall?.risk_level,
      outcome: overall?.predicted_outcome,
    };
  }

  if (display.key === "passed") {
    const reviewCount = summary.review || 0;
    return {
      key: "passed",
      label: display.label,
      shortLabel: display.shortLabel,
      description:
        headline ||
        (reviewCount > 0
          ? `Readiness ${formatReadinessPercent(readiness) || "90%+"}. ${reviewCount} item${reviewCount !== 1 ? "s" : ""} to fix — review before approval.`
          : "All checks passed. Review and approve when satisfied."),
      scoreHint: Number.isFinite(readiness)
        ? `Readiness ${formatReadinessPercent(readiness)}`
        : null,
      tone: "emerald",
      readiness,
      rejectProb,
      risk: overall?.risk_level,
      outcome: overall?.predicted_outcome,
      hasReviewItems: reviewCount > 0,
      reviewCount,
    };
  }

  if (display.key === "review") {
    const issues = summary.issues || summary.review || 0;
    const belowThreshold =
      Number.isFinite(readiness) && !isReadinessPassed(readiness);
    const description =
      headline ||
      (belowThreshold && issues > 0
        ? `${issues} check${issues !== 1 ? "s" : ""} need attention and readiness is ${formatReadinessPercent(readiness) || "below 90%"}.`
        : belowThreshold
          ? `Readiness is ${formatReadinessPercent(readiness) || "below 90%"} — review before approval.`
          : `${issues} check${issues !== 1 ? "s" : ""} may need attention before approval.`);

    return {
      key: "review",
      label: display.label,
      shortLabel: display.shortLabel,
      description,
      scoreHint: Number.isFinite(rejectProb)
        ? `Reject risk ${formatReadinessPercent(rejectProb)}`
        : null,
      tone: "amber",
      readiness,
      rejectProb,
      risk: overall?.risk_level,
      outcome: overall?.predicted_outcome,
    };
  }

  return resolveAiAnomalyResultState({ anomaly_score });
};

/**
 * Human-readable outcome for batch panel, tooltips, and drawer (legacy path).
 */
export const resolveAiAnomalyResultState = ({
  success = true,
  failed = false,
  anomaly_score,
  failMessage,
} = {}) => {
  if (failed || success === false) {
    return {
      key: "failed",
      label: "Analysis unavailable",
      shortLabel: "Unavailable",
      description: failMessage || "AI could not complete this check.",
      scoreHint: null,
      tone: "red",
    };
  }

  const score = Number(anomaly_score);
  if (!Number.isFinite(score)) {
    return {
      key: "unknown",
      label: "Analysed",
      shortLabel: "Done",
      description: "AI finished but no risk score was returned.",
      scoreHint: null,
      tone: "gray",
    };
  }

  if (score >= AI_ANOMALY_FLAG_THRESHOLD) {
    return {
      key: "needs_review",
      label: "Needs review",
      shortLabel: "Review",
      description: `Possible issues detected (risk score ${formatAiScore(score)}). Open the report for details.`,
      scoreHint: `Risk ${formatAiScore(score)}`,
      tone: "amber",
    };
  }

  if (score <= 0) {
    return {
      key: "clear",
      label: "No issues found",
      shortLabel: "Clear",
      description: "AI did not detect any anomalies on this timesheet.",
      scoreHint: "No risk flagged",
      tone: "emerald",
    };
  }

  return {
    key: "low_risk",
    label: "Looks good",
    shortLabel: "Clear",
    description: `Low risk score (${formatAiScore(score)}). No action needed.`,
    scoreHint: `Risk ${formatAiScore(score)}`,
    tone: "emerald",
  };
};

export const buildTimesheetRowLabel = (row) => {
  if (!row) return { title: "Timesheet", subtitle: "" };
  const worker = row?.worker
    ? `${row.worker.first_name || ""} ${row.worker.last_name || ""}`.trim()
    : "";
  const parsed = resolveShiftDateForConfirm(row);
  const date = parsed ? parsed.format("dddd, DD MMMM YY") : "";
  const participant =
    row?.participants?.length === 1
      ? `${row.participants[0]?.first_name || ""} ${row.participants[0]?.last_name || ""}`.trim()
      : row?.participants?.length > 1
        ? `${row.participants.length} participants`
        : "";
  const subtitle = [date, participant].filter(Boolean).join(" · ");
  return {
    title: worker || `Timesheet #${row?.id || ""}`,
    subtitle,
  };
};

export const truncateAiText = (text, max = 72) => {
  const value = String(text || "").trim();
  if (!value) return "";
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
};

/** Remove markdown bold markers for plain UI tooltips. */
export const stripAiMarkdown = (text) =>
  String(text || "")
    .replace(/\*\*/g, "")
    .replace(/\[(USER_INPUT_START|USER_INPUT_END)\]/gi, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Split NDIS approver summary into readable paragraphs (pairs of sentences).
 * Preserves full text for quick-scan and detail views.
 */
export const formatReportSummaryParagraphs = (summary) => {
  const clean = stripAiMarkdown(summary);
  if (!clean) return [];

  const sentences = clean
    .split(/(?<=[.!?])\s+(?=[A-Z\[])/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (sentences.length <= 1) return [clean];

  const paragraphs = [];
  for (let i = 0; i < sentences.length; i += 2) {
    paragraphs.push(sentences.slice(i, i + 2).join(" "));
  }
  return paragraphs;
};

export const isDevFallbackAiContent = (text) =>
  /development llm fallback|dev_fallback|configure openai_api_key/i.test(
    String(text || "")
  );

export const humanizeCheckId = (id) =>
  String(id || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const buildAiStatusTooltip = (status) => {
  if (!status) return "";
  if (status.failed) {
    return `${status.failMessage || "Analysis unavailable"} · Click for details`;
  }
  if (!status.hasReport) return "";
  const resultState =
    status.headline || status.checkSummary
      ? resolveAiOverallUiState({
          overall: {
            headline: status.headline,
            ready_to_approve: status.ready_to_approve,
            readiness_score: status.readiness_score,
            risk_level: status.risk_level,
            reject_probability: status.reject_probability,
          },
          checkSummary: status.checkSummary,
          anomaly_score: status.anomaly_score,
        })
      : resolveAiAnomalyResultState({
          anomaly_score: status.anomaly_score,
        });
  const summary = status.checkSummary;
  const checkLine =
    summary && summary.total > 0
      ? `${summary.pass}/${summary.total} passed${
          summary.critical > 0 ? ` · ${summary.critical} critical` : ""
        }${summary.review > 0 ? ` · ${summary.review} to review` : ""}`
      : "";
  const issuesLine = status.topIssuesLine || formatTopAiIssuesLine(status.topIssues);
  const parts = [
    status.headline || resultState.label,
    checkLine,
    issuesLine,
    resultState.scoreHint,
  ].filter(Boolean);
  return `${parts.join(" · ")} · Click to view report`;
};
